// src/services/userService.js
//
// Phase 4 — User profiles, roles, parent ↔ student linkage.
// ---------------------------------------------------------
// Every Firebase Auth user has a companion document at
// `users/{uid}` that stores their role and (for parents) the list of
// student ids they are linked to. The teacher app never requires
// this doc to exist (falls back to a teacher role) so the Phase-4
// rollout is fully backwards compatible with Phases 1-3 accounts.

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const ROLES = {
  ADMIN: 'admin',
  TEACHER: 'teacher',
  PARENT: 'parent',
  STUDENT: 'student'
};

/** Everyone who can use the teacher/admin workspace. */
export const STAFF_ROLES = [ROLES.ADMIN, ROLES.TEACHER];

export const isStaffRole = (role) => STAFF_ROLES.includes(role);
export const isParentRole = (role) => role === ROLES.PARENT;
export const isStudentRole = (role) => role === ROLES.STUDENT;

const USERS_COLLECTION = 'users';

export const userService = {
  /**
   * Fetch a user profile. Returns `null` when the doc does not exist,
   * so the caller can decide whether to create one or fall back to a
   * default (teacher) role.
   */
  async getUserProfile(uid) {
    if (!uid) return null;
    try {
      const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      // Do not throw — a missing profile should never break login.
      console.warn('getUserProfile failed:', error.message);
      return null;
    }
  },

  /**
   * Create or merge a user profile document. Never overwrites the
   * `createdAt` timestamp once set.
   */
  async upsertUserProfile(uid, data = {}) {
    if (!uid) throw new Error('upsertUserProfile: uid is required');
    const ref = doc(db, USERS_COLLECTION, uid);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      await updateDoc(ref, { ...data, updatedAt: Timestamp.now() });
    } else {
      await setDoc(ref, {
        role: data.role || ROLES.TEACHER,
        linkedStudentIds: [],
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
    return uid;
  },

  /** Seed a new parent account linked to one or more students. */
  async createParentAccount(uid, { displayName, phone, email, linkedStudentIds = [] }) {
    return userService.upsertUserProfile(uid, {
      role: ROLES.PARENT,
      displayName: displayName || null,
      phone: phone || null,
      email: email || null,
      linkedStudentIds
    });
  },

  async setUserRole(uid, role) {
    if (!Object.values(ROLES).includes(role)) {
      throw new Error(`setUserRole: unknown role "${role}"`);
    }
    return userService.upsertUserProfile(uid, { role });
  },

  /**
   * Add a student to the parent's linked list. Idempotent thanks to
   * Firestore's `arrayUnion`.
   */
  async linkStudentToParent(parentUid, studentId) {
    if (!parentUid || !studentId) return;
    await updateDoc(doc(db, USERS_COLLECTION, parentUid), {
      linkedStudentIds: arrayUnion(studentId),
      updatedAt: Timestamp.now()
    });
  },

  async unlinkStudentFromParent(parentUid, studentId) {
    if (!parentUid || !studentId) return;
    await updateDoc(doc(db, USERS_COLLECTION, parentUid), {
      linkedStudentIds: arrayRemove(studentId),
      updatedAt: Timestamp.now()
    });
  },

  /**
   * Look up every parent profile that has `studentId` in its
   * `linkedStudentIds` array. Used by the messaging service when a
   * new session is logged and we need to figure out who to notify.
   */
  async getParentsForStudent(studentId) {
    if (!studentId) return [];
    try {
      const q = query(
        collection(db, USERS_COLLECTION),
        where('role', '==', ROLES.PARENT),
        where('linkedStudentIds', 'array-contains', studentId)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('getParentsForStudent failed:', error.message);
      return [];
    }
  }
};

/**
 * Pure helper — given a role string (possibly null/undefined), returns
 * a safe default so the UI can always branch on a known value.
 * Missing role => treat as TEACHER so legacy Phase-1 accounts keep
 * working exactly as before.
 */
export const resolveRole = (role) => {
  if (!role) return ROLES.TEACHER;
  if (!Object.values(ROLES).includes(role)) return ROLES.TEACHER;
  return role;
};
